import { useCallback, useMemo } from "react";
import { Button, Flex, Icon, Icons, type TableColumnType } from "@ir/ui";
import {
  MasterDataDeductionCategory,
  type IMasterDataDeductionCategory,
  type MasterDataDeductionCategory as MasterDataDeductionCategoryEntity,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  MasterDataListTable,
  createMasterDataLinkedSelectionColumn,
  renderMasterDataLinkedNameCell,
  renderMasterDataStatusChip,
} from "./masterDataTable.shared";

export interface MasterDataDeductionCategoryRow
  extends IMasterDataDeductionCategory {
  key: string;
}

function mapCategoriesToRows(
  categories: MasterDataDeductionCategoryEntity[],
): MasterDataDeductionCategoryRow[] {
  return categories.map((entity) => ({
    ...MasterDataDeductionCategory.toJSON(entity),
    key: String(entity.categoryId),
  }));
}

interface BuildColumnsArgs {
  onEdit: (category: MasterDataDeductionCategoryEntity) => void;
  categoriesById: Map<number, MasterDataDeductionCategoryEntity>;
}

function buildColumns({
  onEdit,
  categoriesById,
}: BuildColumnsArgs): TableColumnType<MasterDataDeductionCategoryRow>[] {
  return [
    {
      title: "Category Name",
      dataIndex: "categoryName",
      key: "categoryName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 260,
      fixed: "left",
      cellRender: (value: unknown, record: MasterDataDeductionCategoryRow) =>
        renderMasterDataLinkedNameCell(value, record),
    },
    {
      title: "Category Key",
      dataIndex: "categoryKey",
      key: "categoryKey",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 200,
      fixed: "left",
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 150,
      cellRender: renderMasterDataStatusChip,
    },
    {
      title: "Actions",
      dataIndex: "categoryId",
      key: "actions",
      align: "center",
      fixed: "right",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 80,
      cellRender: (value: unknown) => {
        const id = Number(value);
        const category = categoriesById.get(id);
        if (!category) return null;
        return (
          <HasAnyPermission
            requiredPermissions={[Permissions.MASTER_DATA_EDIT]}
          >
            <Flex align="center" justifyContent="center">
              <Button
                size="small"
                variant="outline"
                color="grey"
                aria-label={`Edit ${category.categoryName}`}
                onPress={() => onEdit(category)}
              >
                <Icon name={Icons.Edit} size="small" />
              </Button>
            </Flex>
          </HasAnyPermission>
        );
      },
    },
  ];
}

interface MasterDataDeductionCategoriesTableProps {
  deductionCategories: MasterDataDeductionCategoryEntity[];
  search: string;
  setSearch: (search: string) => void;
  onEdit: (category: MasterDataDeductionCategoryEntity) => void;
  showSelection: boolean;
  selectedIds: ReadonlySet<number>;
  onSelectionChange: (selectedIds: Set<number>) => void;
}

export const MasterDataDeductionCategoriesTable = ({
  deductionCategories,
  search,
  setSearch,
  onEdit,
  showSelection,
  selectedIds,
  onSelectionChange,
}: MasterDataDeductionCategoriesTableProps) => {
  const categoriesById = useMemo(() => {
    const map = new Map<number, MasterDataDeductionCategoryEntity>();
    for (const category of deductionCategories) {
      map.set(category.categoryId, category);
    }
    return map;
  }, [deductionCategories]);

  const dataSource = useMemo(
    () => mapCategoriesToRows(deductionCategories),
    [deductionCategories],
  );

  const selectedRowKeys = useMemo(
    () => Array.from(selectedIds).map(String),
    [selectedIds],
  );

  const selectableKeys = useMemo(
    () =>
      deductionCategories
        .filter((category) => !category.isLinked)
        .map((category) => String(category.categoryId)),
    [deductionCategories],
  );

  const handleSelectionChange = useCallback(
    (keys: Set<string>) => {
      const next = new Set<number>();
      for (const key of keys) {
        const id = Number(key);
        const category = categoriesById.get(id);
        if (category && !category.isLinked) {
          next.add(id);
        }
      }
      onSelectionChange(next);
    },
    [categoriesById, onSelectionChange],
  );

  const columns = useMemo(() => {
    const dataColumns = buildColumns({
      onEdit,
      categoriesById,
    });
    if (!showSelection) return dataColumns;
    return [
      createMasterDataLinkedSelectionColumn<MasterDataDeductionCategoryRow>({
        selectedKeys: new Set(selectedRowKeys),
        selectableKeys,
        onChange: handleSelectionChange,
        getAriaLabel: (record) =>
          record.isLinked
            ? `${record.categoryName} is linked and cannot be selected`
            : `Select ${record.categoryName}`,
      }),
      ...dataColumns,
    ];
  }, [
    categoriesById,
    handleSelectionChange,
    onEdit,
    selectableKeys,
    selectedRowKeys,
    showSelection,
  ]);

  return (
    <MasterDataListTable
      columns={columns}
      dataSource={dataSource}
      search={search}
      entityLabel="deduction categories"
      ariaLabel="Deduction Categories table"
      setSearch={setSearch}
      setPage={() => {}}
    />
  );
};
